import { create } from 'zustand'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'theme'

/**
 * Resolve the startup theme: the reader's own stored choice, else dark.
 *
 * Dark is the product's default rather than the OS preference. This screen is a
 * wall boards and on-call laptops leave open, and the palette in `index.css` is
 * designed dark-first — the light theme is a runtime override of it. Following
 * `prefers-color-scheme` meant a machine set to light opened on the secondary
 * palette without anyone having asked for it; the toggle is still one click, and
 * a click is remembered from then on.
 */
function getInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return 'dark'
}

/** Reflect the theme onto <html data-theme> so the CSS token overrides take effect. */
function apply(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme)
}

/**
 * Apply the initial theme immediately (called from main.tsx before render) so
 * there's no flash of the wrong palette on first paint.
 */
export function initTheme() {
  apply(getInitialTheme())
}

interface ThemeState {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggle: () => void
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: getInitialTheme(),
  setTheme: (theme) => {
    apply(theme)
    localStorage.setItem(STORAGE_KEY, theme)
    set({ theme })
  },
  toggle: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
}))
